import type { MachineId, SimEvent, Snapshot } from '../shared/contract';
import { DT } from '../core/const';
import type { RunDirector } from '../core/run';
import { LEHR_BEATS, type LehrBeat } from '../core/lehrnacht';
import { SLOWMO, SLOWMO_SCALE } from '../render-kit/src/anim/tween';
import type { InputMap } from './input';
import type { GameLoop } from './loop';
import type { SlipPrinter } from './slip';

/**
 * DIE LEHRNACHT — the shell's director.
 *
 * *Lehrgeld zahlen*: to pay your dues, to learn the expensive way. This file is the
 * tutorial, and it teaches by letting the player fail cheaply, safely and funnily. It
 * never explains. There is no menu, no "TUTORIAL" label, no progress bar, and the word
 * tutorial does not appear on screen anywhere in the game.
 *
 * ## What this is, structurally
 *
 * A **gate** and a **script**, and nothing else. It cannot touch the sim: it owns no
 * state the world can see, it mutates nothing, and every single thing it makes happen
 * goes out through `RunDirector.push` as an ordinary logged input. That is not tidiness,
 * it is the whole reason the design is allowed to exist:
 *
 *   - **The gate** decides which of the player's inputs reach the sim during a beat. A
 *     refused input is never pushed, so it never enters the log, so a replay of the log
 *     is a replay of what actually happened.
 *   - **The script** pushes `{t:'lehrbeat'}` metas to reshape the arcade between beats
 *     (see `core/lehrnacht.ts`), and pushes nothing else. It does not play the game for
 *     the player. Every coin dropped tonight is dropped by a human hand.
 *
 * So the Lehrnacht is a run like any other run: seed 1848, an input log, a receipt. You
 * can share it, replay it, and the E2E drives it with the same `advance(ticks)` harness
 * it drives everything else with.
 *
 * ## The voice
 *
 * Der Croupier is polite, ancient, self-interested and bored. He is teaching you to
 * gamble because he wants his fifty back, which is the only motivation he has and the
 * only one he needs. ≤ 8 words a line (enforced in `slip.ts`), never two lines at once,
 * never while a ball is in the air. Comedy from specificity; zero winks.
 *
 * ## The one thing that would break it
 *
 * A tutorial that can be soft-locked by a contrarian is not a tutorial. Every beat below
 * therefore has three ways out — do the thing, ignore the thing until he repeats himself,
 * or walk away — and `nudge()` is what makes the second one survivable. The refusal line
 * exists because the most expensive bug in this project's history was a player pressing
 * keys for twenty-eight seconds against a game that had silently decided to ignore them.
 */

/** Hard budget for the happy path, in seconds of game time. Asserted by the E2E. */
export const LEHRNACHT_BUDGET_S = 8 * 60;

/** He repeats himself this often, in seconds, if you do nothing at all. */
const NUDGE_S = 13;

/** …and he stops repeating himself after this many times. He is not your mother. */
const MAX_NUDGES = 3;

/** A refused input earns a line at most this often. Deadpan, not nagging. */
const REFUSE_COOLDOWN_S = 4;

/**
 * How many throws beat 1 waits for its near-miss before moving on regardless.
 *
 * Only two of seven pockets bank into DER TOPF and they sit in a narrow band of the aim
 * space (`./scripts/dev.sh run aim`), so a player who throws softly can miss them all
 * night. He teaches the aim at five and gives up at this. The lesson is not lost: beat 4
 * stands a wall of coins on the pusher's lip and every one of them feeds the pot.
 */
const GIVE_UP_DROPS = 11;

type Verb = 'setzen' | 'ruetteln' | 'glueck' | 'kassieren' | 'nachlegen' | 'meta';

export interface LehrnachtOpts {
  dir: RunDirector;
  input: InputMap;
  loop: GameLoop;
  slips: SlipPrinter;
  /** the Hausregeln board, so it can glow when a rule prints onto it */
  boardEl: () => HTMLElement | null;
  /** where the slow-motion payout ticker prints */
  root: HTMLElement;
  /** "Ich kenne mich aus." — straight to a real run */
  onSkip: () => void;
  /** the night is over: frame the receipt in Die Vitrine */
  onFinish: (receipt: string[]) => void;
}

/** One beat of the night. `enter` speaks, `allow` gates, `step` decides when it is over. */
interface Beat {
  id: LehrBeat;
  /** the world-shape verb pushed on entry, if this beat changes the room */
  push?: LehrBeat;
  enter(d: Lehrnacht): void;
  /** may this input through right now? */
  allow(d: Lehrnacht, verb: Verb, machine: MachineId, payload?: unknown): boolean;
  /** called every frame; return true when the beat is complete */
  step(d: Lehrnacht, s: Snapshot, ev: readonly SimEvent[]): boolean;
  /** what he says when you do nothing */
  nudge?: string;
}

export class Lehrnacht {
  private readonly o: LehrnachtOpts;
  private i = 0;
  private beatStartTick = 0;
  private lastNudgeTick = 0;
  private nudges = 0;
  private lastRefuseTick = 0;
  private finished = false;
  private skipped = false;
  private snap: Snapshot | null = null;

  /** per-beat scratch: every beat gets a clean bag, so beats cannot leak into each other */
  private mem: Record<string, number | boolean | string> = {};

  /** game-time seconds at which each beat ended — the clock the E2E asserts */
  readonly beatClock: { beat: string; at: number }[] = [];

  private tickerEl: HTMLElement;
  private slowUntil = 0;

  constructor(o: LehrnachtOpts) {
    this.o = o;
    this.tickerEl = document.createElement('div');
    this.tickerEl.className = 'sh-ticker';
    o.root.appendChild(this.tickerEl);
    this.enterBeat();
  }

  // ------------------------------------------------------------------ public surface

  get beat(): LehrBeat {
    return BEATS[Math.min(this.i, BEATS.length - 1)]!.id;
  }

  get index(): number {
    return this.i;
  }

  get over(): boolean {
    return this.finished || this.skipped;
  }

  /** Game-time seconds since the doors closed. */
  get seconds(): number {
    return (this.snap?.tick ?? 0) * DT;
  }

  /** Everything the E2E wants to know, in one object. */
  state(): Record<string, unknown> {
    return {
      beat: this.beat,
      index: this.i,
      seconds: Number(this.seconds.toFixed(2)),
      over: this.over,
      skipped: this.skipped,
      finished: this.finished,
      clock: this.beatClock,
      lines: this.o.slips.log,
      /** the current beat's scratch memory — what it is waiting for, in its own words */
      mem: { ...this.mem },
    };
  }

  dispose(): void {
    this.tickerEl.remove();
    this.o.loop.timescale = 1;
  }

  /**
   * THE GATE. `InputMap` asks this before every push, so a refused input never reaches
   * the log and never reaches the sim.
   *
   * Two things are always allowed, in every beat, and both are deliberate: **leaving a
   * cabinet** and **walking**. A tutorial that traps you at a machine is a tutorial you
   * resent, and the whole design of this arcade is that walking away is legal.
   */
  gate(verb: Verb, machine: MachineId, payload?: unknown): boolean {
    if (this.over) return true;
    if (verb === 'meta') {
      const t = (payload as { t?: string } | undefined)?.t;
      if (t === 'walk' || t === 'leave' || t === 'lehrbeat') return true;
    }
    const beat = BEATS[this.i];
    if (!beat) return true;
    if (beat.allow(this, verb, machine, payload)) return true;
    this.refuse(verb);
    return false;
  }

  /** Called once a frame from the game loop, after the sim has stepped. */
  observe(s: Snapshot): void {
    this.snap = s;
    if (this.over) return;

    // hand the slip printer the "is physics happening" question. He does not talk over
    // a ball in flight (ART-BIBLE §7.3: one thing at a time).
    this.o.slips.hold(this.busyPhysics(s));

    if (this.slowUntil > 0 && s.tick >= this.slowUntil) {
      this.slowUntil = 0;
      this.o.loop.timescale = 1;
    }

    const beat = BEATS[this.i];
    if (!beat) return;
    if (beat.step(this, s, s.events)) {
      this.beatClock.push({ beat: beat.id, at: Number(this.seconds.toFixed(2)) });
      this.i++;
      this.enterBeat();
      return;
    }
    this.maybeNudge(s, beat);
  }

  // ------------------------------------------------------------------ beat plumbing

  private enterBeat(): void {
    const beat = BEATS[this.i];
    this.mem = {};
    this.nudges = 0;
    this.beatStartTick = this.snap?.tick ?? 0;
    this.lastNudgeTick = this.beatStartTick;
    if (!beat) {
      this.finished = true;
      return;
    }
    if (beat.push) this.pushBeat(beat.push);
    beat.enter(this);
  }

  /** Re-assert a beat's world shape. Idempotent in core; see `applyBeat`. */
  pushBeat(beat: LehrBeat): void {
    this.o.dir.push('meta', 'pachinko', { t: 'lehrbeat', beat });
  }

  say(text: string, prompt?: string, cold = false): void {
    this.o.slips.say({ text, prompt, cold });
  }

  /** Pin an option to the cabinet. It appears at once and does not expire. */
  choice(text: string, label: string, on: () => void, note?: string): void {
    this.o.slips.pin({ text, label, on, note });
  }

  /** The option is no longer on the table. */
  unpin(): void {
    this.o.slips.unpin();
  }

  skip(): void {
    if (this.over) return;
    this.skipped = true;
    this.o.slips.clear();
    this.o.loop.timescale = 1;
    this.o.onSkip();
  }

  finish(receipt: string[]): void {
    if (this.finished) return;
    this.finished = true;
    this.o.loop.timescale = 1;
    this.o.onFinish(receipt);
  }

  get dir(): RunDirector {
    return this.o.dir;
  }

  get input(): InputMap {
    return this.o.input;
  }

  /** Take the current slip away early — the player did the thing, the line is stale. */
  slipsAdvance(): void {
    this.o.slips.advance();
  }

  /** A slip is on screen or waiting to be. Beats wait for quiet before they speak. */
  get slipsBusy(): boolean {
    return this.o.slips.busy;
  }

  get slipsQuiet(): boolean {
    return !this.o.slips.busy;
  }

  /** Beat-local scratch memory. Cleared on every beat change. */
  once(key: string): boolean {
    if (this.mem[key]) return false;
    this.mem[key] = true;
    return true;
  }

  remember(key: string, v: number | string | boolean): void {
    this.mem[key] = v;
  }

  recall(key: string): number | string | boolean | undefined {
    return this.mem[key];
  }

  /** Seconds of game time spent in the current beat. */
  get inBeat(): number {
    return ((this.snap?.tick ?? 0) - this.beatStartTick) * DT;
  }

  /**
   * **A bounded "let that land" timer, and the reason it exists instead of the obvious
   * thing.**
   *
   * Every beat used to finish with `return d.inBeat > 1 && !d.slipsBusy` — wait for his
   * comment to finish printing, then move on. It reads perfectly and it can hang forever:
   * the slip printer is *held* while a ball is in the air (§7.3, he does not talk over
   * physics), so a player who keeps throwing keeps the queue held, `busy` never goes
   * false, and the beat never ends. Diagnosed with `e2e/probe-lehr.mjs`, which printed
   * `topfDone: true` on throw ten and `beat: wurf` on throws eleven, twelve and thirteen —
   * the lesson had landed and the beat was waiting on a condition the player was actively
   * preventing.
   *
   * So completion waits on the **clock**, which cannot be held. Returns false the first
   * time it is asked (starting the timer) and true once `seconds` of game time have gone.
   */
  settled(key: string, seconds: number): boolean {
    const k = `@${key}`;
    const now = this.snap?.tick ?? 0;
    const at = this.mem[k];
    if (typeof at !== 'number') {
      this.mem[k] = now;
      return false;
    }
    return (now - at) * DT >= seconds;
  }

  private maybeNudge(s: Snapshot, beat: Beat): void {
    if (!beat.nudge || this.nudges >= MAX_NUDGES) return;
    if ((s.tick - this.lastNudgeTick) * DT < NUDGE_S) return;
    if (this.o.slips.busy) return;
    this.lastNudgeTick = s.tick;
    this.nudges++;
    /**
     * **A nudge has to know where the player is standing.**
     *
     * Every beat but the first and the last assumes hands on a machine, and their nudges
     * are written that way — „Noch einer.", „K oder N." Say either of those to somebody
     * standing in the aisle and you have told them to do something they cannot do, which
     * is worse than saying nothing. The contrarian pass walks out of cabinets mid-lesson
     * (legally: leaving is always allowed) and then sat through three nudges telling it to
     * throw another ball at a machine it was nowhere near.
     */
    const adrift = s.run.phase === 'aisle' && beat.id !== 'tuer' && beat.id !== 'schluss';
    this.say(adrift ? 'Zurück an den Automaten.' : beat.nudge);
  }

  /**
   * A verb key was pressed with the player's hands on nothing.
   *
   * `InputMap` returns early in that case and pushes nothing, which is correct — and is
   * also exactly where wave 4's twenty-eight seconds of silence came from. During the
   * Lehrnacht that silence is unaffordable: a new player pressing R in the aisle and
   * getting nothing has learned that the game is broken, not that they are in the wrong
   * place.
   */
  ignored(_verb: string): void {
    this.refuse('meta', 'Sie stehen an keinem Automaten.');
  }

  private refuse(verb: Verb, line?: string): void {
    const tick = this.snap?.tick ?? 0;
    if ((tick - this.lastRefuseTick) * DT < REFUSE_COOLDOWN_S) return;
    if (this.o.slips.busy) return;
    this.lastRefuseTick = tick;
    // He is not confused and he is not cross. He is waiting.
    this.say(line ?? (verb === 'meta' ? 'Später. Erst hier.' : 'Eins nach dem anderen.'));
  }

  /** Is something moving that the player is watching? */
  private busyPhysics(s: Snapshot): boolean {
    const cab = s.cabinet;
    if (!cab) return false;
    if (cab.machine !== 'pachinko') return false;
    // a ball above the pockets and awake is a ball being watched
    for (const t of cab.tokens) {
      if (t.asleep) continue;
      if (t.pos[1] - cab.origin[1] > 2) return true;
    }
    return false;
  }

  /** Make the Hausregeln board glow: a rule just printed onto it. */
  glowBoard(): void {
    const el = this.o.boardEl();
    if (!el) return;
    el.classList.add('sh-board-glow');
    setTimeout(() => el.classList.remove('sh-board-glow'), 2600);
  }

  /**
   * THE PAYOUT TICKER — the only slow-motion in this game, ever.
   *
   * economy.md's promise is that this house shows its math, and `payoutStage` events
   * carry the whole sum: base, material, STRÄHNE, TOPF, floor, each with the number
   * before it and the number after. Once, on the first score of the player's life, the
   * game stops and prints the arithmetic a line at a time. Never again — a game that
   * slows down for every payout is a game with no payouts left to slow down for.
   */
  runTicker(s: Snapshot, all: { stage: string; before: number; after: number }[]): void {
    /**
     * ONE payout, not every payout in the frame.
     *
     * The E2E steps the sim in chunks, and a chunk can contain two scored pockets — the
     * first screenshot of this ticker showed **ten** rows, two complete pipelines stacked
     * on top of each other, which is not "the arithmetic of your first win", it is a
     * spreadsheet. The pipeline always runs base → material → straehne → topf → floor, so
     * the second appearance of `base` is the second payout, and it is not this lesson.
     */
    const stages: typeof all = [];
    for (const st of all) {
      if (st.stage === 'base' && stages.length > 0) break;
      stages.push(st);
    }
    if (stages.length === 0) return;
    this.o.loop.timescale = SLOWMO_SCALE;
    this.slowUntil = s.tick + Math.round((SLOWMO / 1000) / DT);
    const el = this.tickerEl;
    el.innerHTML = '';
    el.classList.add('on');
    const title = document.createElement('div');
    title.className = 'sh-ticker-title';
    title.textContent = 'SO RECHNET DAS HAUS';
    el.appendChild(title);
    let i = 0;
    const step = (): void => {
      if (i >= stages.length) {
        setTimeout(() => el.classList.remove('on'), 2600);
        return;
      }
      const st = stages[i++]!;
      const row = document.createElement('div');
      row.className = 'sh-ticker-row';
      const k = document.createElement('span');
      k.textContent = STAGE_WORD[st.stage] ?? st.stage;
      const v = document.createElement('span');
      v.textContent = `${fmt(st.before)} → ${fmt(st.after)}`;
      row.append(k, v);
      el.appendChild(row);
      // one stage per BEAT of real time — inside the slow-motion window this reads as
      // the machine thinking, which is exactly what it is doing
      setTimeout(step, 420);
    };
    step();
  }
}

const STAGE_WORD: Record<string, string> = {
  base: 'Grundwert',
  material: 'Material',
  straehne: 'Strähne',
  topf: 'Der Topf',
  floor: 'Etage',
};

function fmt(n: number): string {
  const v = Math.round(Math.abs(n)).toString();
  let out = '';
  for (let i = 0; i < v.length; i++) {
    if (i > 0 && (v.length - i) % 3 === 0) out += '.';
    out += v[i];
  }
  return (n < 0 ? '-' : '') + out;
}

// ---------------------------------------------------------------------------------
// helpers the beats share

const isMeta = (verb: Verb, payload: unknown, t: string): boolean =>
  verb === 'meta' && (payload as { t?: string } | undefined)?.t === t;

const entering = (payload: unknown, m: MachineId): boolean =>
  (payload as { t?: string; machine?: MachineId } | undefined)?.t === 'enter'
  && (payload as { machine?: MachineId }).machine === m;

const at = (s: Snapshot, m: MachineId): boolean =>
  s.run.activeMachine === m && (s.run.phase === 'machine' || s.run.phase === 'shop');

const has = (ev: readonly SimEvent[], t: SimEvent['t']): boolean => ev.some((e) => e.t === t);

// ---------------------------------------------------------------------------------
// THE FIVE BEATS

const BEATS: readonly Beat[] = [
  /**
   * BEAT 0 — DIE TÜR. The doors close, the sign flickers, he takes your marker.
   *
   * The quiet out lives here and nowhere else: one printed option, no styling that begs
   * to be clicked, and it never appears again. A player who knows this game should be
   * able to leave in four seconds without reading a word of the rest.
   */
  {
    id: 'tuer',
    push: 'tuer',
    enter(d) {
      /**
       * The out is PINNED, not queued, and that ordering is the whole point of it: a
       * player who already knows this game must be able to decline in four seconds
       * without sitting through the fiction. It goes up in the same frame as the first
       * line and stays up until they walk into a machine.
       */
      d.choice(
        'Ich will sie wiederhaben.',
        'Ich kenne mich aus.',
        () => d.skip(),
        'Oder: E am Kugelregen, und Sie sind dran.',
      );
      d.say('Die Tür ist zu.');
      d.say('Fünfzig Münzen. Bis Sonnenaufgang.');
    },
    allow(_d, verb, machine, payload) {
      return verb === 'meta' && entering(payload, 'pachinko') && machine === 'pachinko';
    },
    step(d, s) {
      if (!at(s, 'pachinko')) return false;
      // he has your marker; the out is no longer on the table
      d.unpin();
      d.slipsAdvance();
      return true;
    },
    nudge: 'Der Kugelregen. Links. E.',
  },

  /**
   * BEAT 1 — DER ERSTE WURF. Aim, click, drop.
   *
   * Two things have to land here and they are in this order for a reason: first the
   * payout is *shown* (the ticker, once, in slow motion), then the near-miss is *banked*
   * (DER TOPF slurps it). Reversed, the pot is a mystery gauge filling for no reason.
   *
   * The near-miss is not hunted for in the seed. Two of the seven pockets flank the
   * jackpot mouth and bank their near-miss, so it arrives on its own within a few drops,
   * and the beat simply waits — prompting, never demanding a particular aim. A tutorial
   * that requires a specific mouse position is a tutorial that can be failed.
   */
  {
    id: 'wurf',
    push: 'wurf',
    enter(d) {
      d.say('Zielen Sie. Dann drücken Sie.', 'Maus = Winkel und Kraft · Klick oder Leertaste = Setzen');
    },
    allow(_d, verb) {
      return verb === 'setzen';
    },
    step(d, s, ev) {
      // ---- the ticker, once, on the first score of the player's life
      const stages = ev.filter((e) => e.t === 'payoutStage') as Extract<SimEvent, { t: 'payoutStage' }>[];
      if (stages.length > 0 && d.once('ticker')) {
        d.runTicker(s, stages.map((e) => ({ stage: e.stage, before: e.before, after: e.after })));
        d.remember('tickerAt', s.tick);
      }
      if (!d.recall('ticker')) return false;

      // ---- then the near-miss. "Almost" is banked, not exploited.
      if (has(ev, 'topfFeed')) {
        if (d.once('topf')) d.say('Fast zählt auch. In den Topf.');
        d.remember('topfDone', true);
      }
      // let his line land before the room changes under it — on the clock, not on the
      // printer, which the player can hold indefinitely by throwing (see `settled`)
      if (d.recall('topfDone')) return d.settled('topf', 2.5);

      /**
       * **The near-miss cannot be waited for indefinitely, and this is the bug the
       * contrarian pass found.**
       *
       * Only two of the seven pockets flank the jackpot and bank into DER TOPF, and
       * `./scripts/dev.sh run aim` shows they sit in a narrow band of the aim space —
       * high power, just off centre. A player who happens to throw softly lands in p5 and
       * p6 all night and *never* sees the pot fill. Measured: the contrarian bot, sweeping
       * low powers, took sixteen drops without one. So he teaches the aim, and then, if
       * they still have not found it, the night moves on rather than stalling on a pocket.
       *
       * The lesson is not lost when that happens: beat 4 stands a wall of coins teetering
       * on the pusher's lip, and every one of them feeds the pot in plain sight.
       */
      if (has(ev, 'score')) {
        const n = Number(d.recall('drops') ?? 0) + 1;
        d.remember('drops', n);
        if (n === 5) d.say('Mehr Kraft. Knapp neben die Mitte.');
        else if (n < GIVE_UP_DROPS && d.slipsQuiet) d.say('Noch einer.');
      }
      /**
       * OUTSIDE the `score` branch, and that is the whole point. The first version put
       * this check inside it and returned `!slipsBusy` — so on the eleventh drop, if a
       * slip happened to be printing, the beat declined to finish and then never asked
       * again, because there were no further score events to ask on. It stalled exactly
       * the player it was written to rescue. Any "am I done yet?" that can answer *not
       * yet* must be reachable on a frame where nothing happened.
       */
      if (Number(d.recall('drops') ?? 0) >= GIVE_UP_DROPS) return d.settled('aufgeben', 2);
      return false;
    },
    nudge: 'Noch einer. Die Kugel kostet nichts.',
  },

  /**
   * BEAT 2 — DIE DUNKLEN KÜNSTE. The shove that works, then the shove that does not.
   *
   * This is the flagship lesson of the whole night and it is a *failure*. The first
   * Rütteln nudges a falling ball and feels illegal, which is the hook. The second one
   * tilts the machine, blows a raspberry, and locks it for four seconds — and costs
   * nothing at all. That is Lehrgeld: the player has now been punished by this game once,
   * knows exactly what it feels like, and is out precisely nothing.
   *
   * The machine is at 0.99 on the TILT gauge when the second shove is asked for, and the
   * gauge is on screen. Nothing is hidden. See `core/lehrnacht.ts` for why the meter is
   * pre-loaded rather than shoved up five times.
   */
  {
    id: 'kuenste',
    push: 'kuenste',
    enter(d) {
      d.say('Der Automat wackelt. Das wissen Sie.');
      d.say('Werfen Sie. Dann drücken Sie R.', 'R = Rütteln, während die Kugel fällt');
    },
    allow(d, verb) {
      if (verb === 'setzen') return !d.recall('shoved');
      return verb === 'ruetteln';
    },
    step(d, s, ev) {
      // ---- the shove that works
      if (has(ev, 'tilt') && !d.recall('shoved')) {
        d.remember('shoved', true);
        d.say('Sehen Sie. Niemand hat es gesehen.');
        return false;
      }
      if (!d.recall('shoved')) return false;

      // ---- the shove that does not
      if (has(ev, 'tiltLockout')) {
        d.say('Nicht so laut.', 'TILT. Der Automat sperrt kurz.', true);
        d.remember('tilted', true);
      }
      if (d.recall('tilted')) return d.settled('tilt', 3);

      /**
       * KEEP THE MACHINE AT THE LINE WHILE HE ASKS.
       *
       * The TILT meter decays at `TILT_DECAY_PER_SEC = 0.06`, and the player is reading a
       * slip that takes about three seconds to ratchet out. The first version armed the
       * meter once, at the moment of the first shove, and then asked for the second one —
       * by which time the playtest measured the gauge at **0.46**, so the second shove
       * landed on a half-empty meter and the raspberry, which is the flagship lesson of
       * the entire night, silently did not happen. A contrarian who wandered off for
       * twenty seconds defeated it completely.
       *
       * So it is re-armed while the question is open, at most once a second of game time.
       * Every re-arm is an ordinary logged `lehrbeat`, so the night still replays exactly.
       */
      const cab = s.cabinet;
      const armAt = Number(d.recall('armAt') ?? 0);
      if (cab && cab.machine === 'pachinko' && cab.tilt01 < 0.92 && (s.tick - armAt) * DT > 1) {
        d.remember('armAt', s.tick);
        d.pushBeat('zuviel');
      }

      if (!d.recall('asked') && d.slipsQuiet) {
        d.remember('asked', true);
        d.say('Noch mal. Ruhig.', 'R');
      }
      return false;
    },
    nudge: 'R. Noch einmal.',
  },

  /**
   * BEAT 3 — DIE ERSTE REGEL. The Preistheke, one parcel, and the board.
   *
   * core-loop.md's opening gate demands a *rule-changing* item, never a token, and it is
   * right: the moment this game becomes itself is the moment the player understands that
   * the objects rewrite the machines. So: one wrapped parcel, a claw rigged (on the board,
   * in writing) to grip, one drop back at the pachinko, and a coin that comes apart in
   * mid-air while a new line prints on a board that had nothing on it.
   */
  {
    id: 'regel',
    push: 'regel',
    enter(d) {
      d.say('Die Preistheke ist an.');
      d.say('Sechs Tickets. Von mir.', 'E an der Preistheke');
      // The claw reaches 1.7 units. A player who clicks the middle of an empty cabinet
      // grips air, and the first thing this game ever gives them is a miss.
      d.say('Zielen Sie auf das Päckchen.', 'Klick = greifen');
    },
    allow(d, verb, machine, payload) {
      if (d.recall('got')) {
        // second half: back to the pachinko, one drop
        return (verb === 'meta' && entering(payload, 'pachinko')) || verb === 'setzen';
      }
      if (verb === 'meta') return entering(payload, 'greifer') || isMeta(verb, payload, 'buy');
      return verb === 'setzen' && machine === 'greifer';
    },
    step(d, s, ev) {
      if (!d.recall('got')) {
        for (const e of ev) {
          if (e.t === 'grab' && e.kept && d.once('grabbed')) {
            d.say('Nehmen Sie es mit.', 'Esc — er rückt es an der Tür heraus');
          }
        }
        /**
         * **The chute is not the bag.** `RunDirector.collectShop` runs on `leave`, so a
         * prize sits in the Preistheke's chute until the player walks away from it — a
         * real rule of this game, not a quirk of the tutorial, and one worth teaching
         * here rather than discovering on floor 3.
         *
         * This watches the *snapshot* rather than an event, and that is not a style
         * choice: `collectShop` emits no `item` event at all (it calls `sim.addItem`
         * directly), so the first version of this beat waited for an event that was never
         * coming and soft-locked the entire night at the shop. Found by playing.
         */
        if (s.run.items.length > 0) {
          d.remember('got', true);
          d.say('Zurück. Einmal werfen.', 'E am Kugelregen');
          d.glowBoard();
        }
        return false;
      }

      // stake the coin that now carries the tag — the player should not have to hunt for
      // it in the drawer on the one drop that is supposed to show them what it does
      const spalt = s.run.beutel.findIndex((t) => t.tags.length > 0);
      if (spalt >= 0) d.input.aim.beutel = spalt;

      // ---- the drop that is visibly a different game
      if (has(ev, 'split')) {
        if (d.once('split')) {
          // Close the mouth in the same frame the coin comes apart. The children are
          // born IN the jackpot mouth, so leaving it wide makes them split again, and
          // again — see `core/lehrnacht.ts` for the two cabinets that measurement cost.
          d.pushBeat('maulzu');
          d.glowBoard();
          d.say('Spaltmarken zerspringen. Steht am Brett.');
        }
        d.remember('done', true);
      }
      if (d.recall('done')) return d.settled('spalt', 3);
      if (has(ev, 'score') && d.recall('got') && d.slipsQuiet && !d.recall('done')) {
        d.say('Noch einer. Mitte.');
      }
      return false;
    },
    nudge: 'Die Preistheke. E.',
  },

  /**
   * BEAT 4 — KASSIEREN ODER NACHLEGEN. The only real decision in the night.
   *
   * GLÜCK is taught here rather than at the pachinko, and that is a deviation from the
   * brief with a reason: on the pachinko the one declared event is the *bust hole*, which
   * does not exist until the player has already doubled down — so teaching GLÜCK there
   * means teaching Nachlegen first, out of order, and then bending a roll the player
   * cannot see. On the coin pusher the declared event is *die Kante*, which is the very
   * line the brief writes, and the bend is visible: the bar hesitates.
   *
   * Then the fork. Both branches are scripted and both are correct. Cashing out is
   * praised, faintly. Doubling down pays — once — because the hook has to be set by a
   * real win and not by a promise. The lesson is not "gamble"; the lesson is that leaving
   * is always legal and staying is always tempting, and the player has now felt both.
   */
  {
    id: 'wahl',
    push: 'wahl',
    enter(d) {
      d.say('Der Schieber. Ihr Sparbuch.', 'E am Münzschieber');
    },
    allow(d, verb, machine, payload) {
      if (verb === 'meta') return entering(payload, 'muenzschieber');
      if (machine !== 'muenzschieber') return false;
      if (!d.recall('glueck')) return verb === 'glueck' || verb === 'setzen';
      if (!d.recall('dropped')) return verb === 'setzen' || verb === 'glueck';
      return verb === 'kassieren' || verb === 'nachlegen' || verb === 'setzen';
    },
    step(d, s, ev) {
      if (!at(s, 'muenzschieber')) return false;

      // ---- GLÜCK, on the one declared event you can watch
      if (d.once('asked-glueck')) d.say('Sagen Sie etwas an. G.', '1 GLÜCK auf: die Kante');
      if (has(ev, 'glueckSpent') && d.once('spent')) {
        d.remember('glueck', true);
        d.say('Angesagt ist angesagt.');
      }
      if (!d.recall('glueck')) {
        // the pusher's pile decays nothing, but a player who left and came back needs the
        // lip rebuilt. `applyBeat` is idempotent and will not double it.
        if (d.inBeat > 25 && d.once('reassert')) d.pushBeat('wahl');
        return false;
      }

      // ---- one drop onto the pile, so the choice is about something they just did
      if (has(ev, 'drop')) d.remember('dropped', true);
      if (!d.recall('dropped')) {
        if (d.once('asked-drop')) d.say('Eine Marke drauf. Klick.');
        return false;
      }

      // ---- the fork
      if (d.once('fork')) d.say('Mitnehmen oder verdoppeln?', 'K = kassieren · N = nachlegen');

      for (const e of ev) {
        if (e.t === 'cashOut' && d.once('k')) {
          d.remember('branch', 'kassiert');
          d.say('Klug. Langweilig, aber klug.');
        }
        if (e.t === 'nachlegen' && d.once('n')) {
          d.remember('branch', 'nachgelegt');
          // the stroke. See `core/lehrnacht.ts` for why this is a stroke and not a seed.
          d.pushBeat('lawine');
        }
      }
      if (d.recall('branch') === 'nachgelegt') {
        if (has(ev, 'jackpot') && d.once('lawine')) d.say('Deshalb bleiben alle sitzen.');
        // do not wait forever on the physics: the coins are already over the lip
        if (d.recall('lawine')) return d.settled('lawine', 3);
        if (Number(d.recall('nAt') ?? 0) === 0) d.remember('nAt', s.tick);
        // the coins are already over the lip; do not wait on physics forever
        if ((s.tick - Number(d.recall('nAt'))) * DT > 12) return true;
        return false;
      }
      if (d.recall('branch') === 'kassiert') return d.settled('kassiert', 2.5);
      return false;
    },
    nudge: 'K oder N. Ihre Entscheidung.',
  },

  /**
   * BEAT 5 — DIE KLEINE ABRECHNUNG. He collects.
   *
   * The confiscation ceremony is shown here on purpose and at the smallest possible
   * stakes, because losing in this game is a *slope* and a player who has never seen the
   * slope will read their first real Abrechnung as a death. What he actually takes is
   * whatever `Economy.settle` decides — the shell reads `confiscated` and names it. It
   * does not tell the player he took a token if he took the pile.
   */
  {
    id: 'schluss',
    push: 'schluss',
    enter(d) {
      d.say('Sonnenaufgang. Das Buch, bitte.', 'Esc — dann im Saal: Abrechnung');
    },
    allow(_d, verb, _m, payload) {
      return verb === 'meta' && (isMeta(verb, payload, 'descend') || isMeta(verb, payload, 'abrechnung'));
    },
    step(d, _s, ev) {
      for (const e of ev) {
        if (e.t === 'abrechnung' && d.once('settled')) {
          if (e.confiscated.length === 0) d.say('Bezahlt. Bis morgen.');
          else d.say(`Als Pfand: ${short(e.confiscated[0]!)}.`, 'Verlieren ist ein Gefälle, kein Absturz.', true);
        }
        if (e.t === 'receipt') {
          d.finish(e.lines);
          return true;
        }
      }
      return false;
    },
    nudge: 'Esc. Dann der Knopf Abrechnung.',
  },
];

/** Trim a confiscation label down to something he would actually say out loud. */
function short(s: string): string {
  return s.replace(/^Automat /, 'Ihr ').replace(/^Haufen im /, 'der Haufen im ');
}

export { BEATS as LEHRNACHT_BEATS, LEHR_BEATS };
